// ══════════════════════════════════════════════════════════
// RFW Backend HTTP Client
//
// Cliente centralizado para consumir el backend PHP
// alojado en el VPS. Toda llamada al backend pasa por aquí.
//
// Reglas:
//  - URL base desde ENV — sin hardcode.
//  - Token Supabase como Bearer automático.
//  - Request ID único por llamada.
//  - Timeout forzado.
//  - Soporte JSON + FormData.
// ══════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import type { BackendResponse, RfwBackendUser } from '@/types/rfwBackend';

const DEFAULT_TIMEOUT_MS = 30_000;

let cachedBaseUrl: string | null = null;
let warnedMissingUrl = false;

function getBaseUrl(): string {
  if (cachedBaseUrl !== null) return cachedBaseUrl;

  const raw = import.meta.env.VITE_RFW_BACKEND_URL as string | undefined;
  const trimmed = raw?.trim() ?? '';
  cachedBaseUrl = trimmed;

  if (!trimmed && !warnedMissingUrl) {
    warnedMissingUrl = true;
    console.warn(
      '[rfwBackend] VITE_RFW_BACKEND_URL no está configurado. ' +
      'Las llamadas al backend PHP fallarán hasta que se defina.',
    );
  }

  return cachedBaseUrl;
}

function buildUrl(path: string): string {
  const base = getBaseUrl();
  if (!base) throw new Error('[rfwBackend] VITE_RFW_BACKEND_URL no configurado');
  const cleanBase = base.replace(/\/+$/, '');
  const cleanPath = path.replace(/^\/+/, '');
  return `${cleanBase}/${cleanPath}`;
}

function generateRequestId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 16; i += 1) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `rfw-${id}`;
}

async function getAccessToken(): Promise<string | null> {
  try {
    const supabase = getSupabaseClient();
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown> | FormData;
  timeout?: number;
  headers?: Record<string, string>;
}

class RfwBackendError extends Error {
  public statusCode: number;
  public responseBody: unknown;

  constructor(message: string, statusCode: number, responseBody?: unknown) {
    super(message);
    this.name = 'RfwBackendError';
    this.statusCode = statusCode;
    this.responseBody = responseBody;
  }
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<BackendResponse<T>> {
  const {
    method = 'GET',
    body,
    timeout = DEFAULT_TIMEOUT_MS,
    headers: extraHeaders = {},
  } = options;

  const url = buildUrl(path);
  const requestId = generateRequestId();
  const token = await getAccessToken();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  const fetchHeaders: Record<string, string> = {
    'X-Request-Id': requestId,
    Accept: 'application/json',
    ...extraHeaders,
  };

  if (token) {
    fetchHeaders.Authorization = `Bearer ${token}`;
  }

  let fetchBody: string | FormData | undefined;

  if (body instanceof FormData) {
    fetchBody = body;
  } else if (body) {
    fetchHeaders['Content-Type'] = 'application/json';
    fetchBody = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, {
      method,
      headers: fetchHeaders,
      body: fetchBody,
      signal: controller.signal,
    });

    clearTimeout(timer);

    const responseText = await response.text();
    let parsed: unknown;

    try {
      parsed = JSON.parse(responseText);
    } catch {
      throw new RfwBackendError(
        `Respuesta no-JSON del backend (${response.status})`,
        response.status,
        responseText,
      );
    }

    if (!response.ok) {
      const err = parsed as Record<string, unknown> | undefined;
      throw new RfwBackendError(
        (err?.message as string) || `Error HTTP ${response.status}`,
        response.status,
        parsed,
      );
    }

    const result = parsed as BackendResponse<T>;

    if (result.success === false) {
      throw new RfwBackendError(
        result.message || 'La operación falló en el backend',
        response.status,
        parsed,
      );
    }

    return result;
  } catch (err: unknown) {
    clearTimeout(timer);

    if (err instanceof RfwBackendError) throw err;

    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new RfwBackendError(
        `Timeout: el backend no respondió en ${timeout}ms`,
        0,
      );
    }

    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new RfwBackendError(
        'No se pudo conectar con el backend. Verificá que el VPS esté accesible.',
        0,
      );
    }

    throw new RfwBackendError(
      err instanceof Error ? err.message : 'Error desconocido al llamar al backend',
      0,
    );
  }
}

// ──────────────────────────────────────────────
// API pública
// ──────────────────────────────────────────────

export const rfwBackend = {
  /**
   * Health check — verifica que el backend PHP esté vivo.
   * GET /health
   */
  health() {
    return request<{
      status: string;
      uptime: number;
      timestamp: string;
      version: string;
      environment: string;
    }>('/health');
  },

  /**
   * Obtiene los datos del usuario autenticado desde el backend PHP.
   * Envía el access token de Supabase como Bearer.
   * GET /api/v1/auth/me
   */
  getAuthenticatedUser() {
    return request<RfwBackendUser>('/api/v1/auth/me');
  },
};

/**
 * Request genérico al backend PHP.
 * Expuesto para que otros servicios (ej. stations.ts) puedan
 * hacer llamadas tipadas sin duplicar la lógica de autenticación,
 * timeouts y manejo de errores.
 */
export async function backendApiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<BackendResponse<T>> {
  return request<T>(path, options);
}

export { RfwBackendError };
export type { RequestOptions };